import { defineStore } from 'pinia';
import { shallowRef, shallowReactive, ref, computed } from 'vue';
import type { Component } from 'vue';

/**
 * Where an older build kept the peek-mode choice, side or center. There is
 * one kind of peek now, side, so a stored `center` is rewritten as `side` the
 * first time the store loads. Nothing reads the choice any more. It is kept
 * as `side` rather than deleted, so a browser that opens an older build still
 * gets a side peek. Spec: docs/specs/record-navigation-layers.md, "The side
 * peek".
 */
export const LEGACY_PEEK_MODE_STORAGE_KEY = 'inspector-view-default-mode';

export function migrateLegacyPeekMode(): void {
    if (typeof localStorage === 'undefined') {
        return;
    }
    try {
        const stored = localStorage.getItem(LEGACY_PEEK_MODE_STORAGE_KEY);
        if (stored !== null && stored !== 'side') {
            localStorage.setItem(LEGACY_PEEK_MODE_STORAGE_KEY, 'side');
        }
    } catch {
        // Storage may be unavailable (private mode, quota); fall through silently.
    }
}

export interface InspectorRouteRef {
    name: string;
    params?: Record<string, string | number>;
}

export interface InspectorView {
    title: string;
    /**
     * Optional secondary line shown next to / below the title in the modal
     * header. Useful for things like artist + track code on a track inspector.
     */
    subtitle?: string;
    component: Component;
    componentProps: Record<string, unknown>;
    route?: InspectorRouteRef | undefined;
    /**
     * Optional component rendered in the header bar in place of the visible
     * title text (the title stays as the screen-reader label). Lets rich
     * inspectors put live content — e.g. a player's status light + zone +
     * time-since-check-in — where "Entity #id" would otherwise sit. Callers
     * should pass a markRaw'd component.
     */
    headerComponent?: Component;
    headerProps?: Record<string, unknown>;
}

export interface InspectorOpenOptions {
    route?: InspectorRouteRef | undefined;
    subtitle?: string;
    headerComponent?: Component;
    headerProps?: Record<string, unknown>;
    /**
     * The record the peek was opened from, when that record is not itself a
     * peek: the record below a table, the card split pane or the full page. The
     * peek's trail starts with its name, and clicking that name closes the
     * stack. Only `open()` reads it, because a pushed view keeps the stack's.
     */
    originTitle?: string;
}

/** One step of the peek's trail: a name, and where clicking it goes. */
export interface InspectorTrailStep {
    title: string;
    /** `origin` is the record under the peeks. `view` is a peek in the stack, at `index`. */
    kind: 'origin' | 'view';
    index: number;
}

export const useInspectorStore = defineStore('inspectorStore', () => {
    migrateLegacyPeekMode();
    const isOpen = ref(false);
    /**
     * Distance (px) from the top of the layout's content region at which the
     * side-peek panel starts. Pages with a persistent toolbar row register
     * its height here (see `useInspectorTopOffset`) so the panel slides in
     * *below* the toolbar instead of covering the page controls.
     */
    const topOffset = ref(0);
    const viewStack = shallowRef<InspectorView[]>([]);
    /**
     * Views popped off `viewStack`, newest last — the browser-style forward
     * history the header's `›` chevron walks. Cleared whenever a new view is
     * pushed or opened (that branches the history).
     */
    const forwardStack = shallowRef<InspectorView[]>([]);
    /** The name of the record the stack was opened from, or null. See InspectorOpenOptions.originTitle. */
    const originTitle = ref<string | null>(null);

    const currentView = computed(() =>
        viewStack.value.length > 0 ? viewStack.value[viewStack.value.length - 1] : null
    );

    const title = computed(() => currentView.value?.title ?? '');
    const subtitle = computed(() => currentView.value?.subtitle ?? '');
    const component = computed(() => currentView.value?.component ?? null);
    const componentProps = computed(() => currentView.value?.componentProps ?? {});
    const headerComponent = computed(() => currentView.value?.headerComponent ?? null);
    const headerProps = computed(() => currentView.value?.headerProps ?? {});
    const canGoBack = computed(() => viewStack.value.length > 1);
    const canGoForward = computed(() => forwardStack.value.length > 0);
    const currentRoute = computed(() => currentView.value?.route);

    /** Open the inspector with a root view (resets any existing stack). */
    function open(
        newTitle: string,
        newComponent: Component,
        newProps: Record<string, unknown> = {},
        options: InspectorOpenOptions = {}
    ) {
        viewStack.value = [
            shallowReactive({
                title: newTitle,
                subtitle: options.subtitle,
                component: newComponent,
                componentProps: newProps,
                route: options.route,
                headerComponent: options.headerComponent,
                headerProps: options.headerProps,
            }),
        ];
        isOpen.value = true;
        // A fresh root view starts a new history.
        forwardStack.value = [];
        originTitle.value = options.originTitle?.trim() ? options.originTitle.trim() : null;
    }

    /** Push a sub-view onto the navigation stack (iOS-style forward nav). */
    function push(
        newTitle: string,
        newComponent: Component,
        newProps: Record<string, unknown> = {},
        options: InspectorOpenOptions = {}
    ) {
        viewStack.value = [
            ...viewStack.value,
            shallowReactive({
                title: newTitle,
                subtitle: options.subtitle,
                component: newComponent,
                componentProps: newProps,
                route: options.route,
                headerComponent: options.headerComponent,
                headerProps: options.headerProps,
            }),
        ];
        isOpen.value = true;
        // Drilling in from here branches the history, so anything we could have
        // gone "forward" to is no longer reachable.
        forwardStack.value = [];
    }

    /**
     * Step back a view, keeping the popped one reachable via {@link forward}.
     *
     * Pass `keepForwardHistory: false` for a view that DISMISSES itself rather
     * than being navigated away from — a "Done" button. Back leaving a forward
     * entry is what a person expects; Done leaving one is odd, because the `›`
     * chevron then lights up pointing at the thing you just said you were
     * finished with.
     */
    function pop(options: { keepForwardHistory?: boolean } = {}) {
        const { keepForwardHistory = true } = options;
        if (viewStack.value.length > 1) {
            const popped = viewStack.value[viewStack.value.length - 1];
            viewStack.value = viewStack.value.slice(0, -1);
            forwardStack.value = keepForwardHistory ? [...forwardStack.value, popped] : [];
        }
    }

    /** Step forward again into the most recently popped view. */
    function forward() {
        if (forwardStack.value.length === 0) {
            return;
        }
        const next = forwardStack.value[forwardStack.value.length - 1];
        forwardStack.value = forwardStack.value.slice(0, -1);
        viewStack.value = [...viewStack.value, next];
    }

    /**
     * Go back to the view at `index` in the stack, as clicking an earlier step of
     * the trail does. The views after it stay reachable with {@link forward}, in
     * order, as if Back had been pressed that many times.
     */
    function popTo(index: number) {
        const stack = viewStack.value;
        if (index < 0 || index >= stack.length - 1) {
            return;
        }
        const popped = stack.slice(index + 1);
        viewStack.value = stack.slice(0, index + 1);
        forwardStack.value = [...forwardStack.value, ...popped.reverse()];
    }

    /**
     * Name a record's views once the record has loaded, so the trail reads
     * "Main St Café" rather than "Location #12". Every view of that record is
     * renamed, including those Back or a trail step has stepped past.
     */
    function renameRecordViews(entityId: string, recordId: number | string, newTitle: string) {
        const trimmed = newTitle.trim();
        if (trimmed === '') {
            return;
        }
        for (const view of [...viewStack.value, ...forwardStack.value]) {
            const props = view.componentProps;
            if (props.entityId === entityId && String(props.recordId) === String(recordId) && view.title !== trimmed) {
                view.title = trimmed;
            }
        }
    }

    /**
     * The trail in the peek's header: the record the stack was opened from, when
     * there is one, then each view in the stack. The last step is the view shown.
     */
    const trail = computed<InspectorTrailStep[]>(() => [
        ...(originTitle.value !== null ? [{ title: originTitle.value, kind: 'origin' as const, index: -1 }] : []),
        ...viewStack.value.map((view, index) => ({ title: view.title, kind: 'view' as const, index })),
    ]);

    /** The record the current view shows, when it shows one, for the full-page button. */
    const currentRecord = computed<{ entityId: string; recordId: number | string } | null>(() => {
        const props = currentView.value?.componentProps;
        if (!props || typeof props.entityId !== 'string') return null;
        const recordId = props.recordId;
        return typeof recordId === 'number' || typeof recordId === 'string' ? { entityId: props.entityId, recordId } : null;
    });

    function close() {
        isOpen.value = false;
        viewStack.value = [];
        forwardStack.value = [];
        originTitle.value = null;
    }

    /** Register the height of the page toolbar the side panel must not cover. */
    function setTopOffset(px: number) {
        topOffset.value = Math.max(0, px);
    }

    return {
        isOpen,
        topOffset,
        setTopOffset,
        title,
        subtitle,
        component,
        componentProps,
        headerComponent,
        headerProps,
        canGoBack,
        canGoForward,
        currentRoute,
        trail,
        currentRecord,
        open,
        push,
        pop,
        popTo,
        forward,
        renameRecordViews,
        close,
    };
});
